import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaHandsHelping } from "react-icons/fa";
import { adminGetAllServices, deleteService } from "redux/features/service/serviceSlice";
import Input from "shared/Input/Input";
import ServiceDetailsPage from "./serviceDetailsPage";
import Loading from "components/Loader/Loading";

export interface ListServicesPageProps {
  className?: string;
}

export interface Service {
  title: string;
  id: number;
  images: string[];
  description?: string;
  events?: string[];
  equipments?: string[];
  region?: string;
  enable?: boolean;
  validation?: boolean;
  createdAt?: string;
  updatedAt?: string;
  serviceAgentId?: number;
  ServiceAgent?: {
    designation?: string;
    availability?: string[];
  };
}

interface ModalState {
  isDeleteModalOpen: boolean;
  isAddModalOpen: boolean;
  isUpdateModalOpen: boolean;
  isViewModalOpen: boolean; // Added for view modal
  selectedItem: Service | null;
  loading: boolean;
}

const ListServicesPage: FC<ListServicesPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    services: servicesList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    services: state.service?.adminAllServices?.services || [],
    totalCount: state.service?.adminAllServices?.totalCount || 0,
    totalPages: state.service?.adminAllServices?.totalPages || 0,
    currentPage: state.service?.adminAllServices?.currentPage || 1,
    loading: state.service?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isDeleteModalOpen: false,
    isAddModalOpen: false,
    isUpdateModalOpen: false,
    isViewModalOpen: false, // Initialize view modal state
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllServices({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleAddModalOpen = () => {
    setModalState({ ...modalState, isAddModalOpen: true });
  };

  const handleUpdateModalOpen = (service: Service) => {
    setModalState({ ...modalState, isUpdateModalOpen: true, selectedItem: service });
  };

  const handleDeleteModalOpen = (service: Service) => {
    setModalState({ ...modalState, selectedItem: service, isDeleteModalOpen: true });
  };

  const handleViewModalOpen = (service: Service) => {
    setModalState({ ...modalState, selectedItem: service, isViewModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isDeleteModalOpen: false,
      isAddModalOpen: false,
      isUpdateModalOpen: false,
      isViewModalOpen: false,
      selectedItem: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteService(modalState.selectedItem.id));
      setModalState({
        ...modalState,
        isDeleteModalOpen: false,
        loading: false,
        selectedItem: null,
      });
      await dispatch(adminGetAllServices({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleTableAction = (action: string, item: Service) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "edit") {
      handleUpdateModalOpen(item);
    } else if (action === "view") {
      handleViewModalOpen(item); // Handle view action
    }
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (item: Service) => (
        <div className="flex items-center justify-center w-16 h-16">
          {item.images && item.images[0] ? (
            <img
              src={`${process.env.REACT_APP_CLOUD_FRONT}${item.images[0]}`}
              alt={item.title}
              className="object-cover w-full h-full rounded"
            />
          ) : (
            <span className="text-center align-middle">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: "title",
      label: "Titre",
      render: (item: Service) => <span className="align-middle">{item.title}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaHandsHelping className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste Des Prestations
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par titre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search services"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {/* Delete Modal */}
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={handleModalClose}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.title || "",
                  }}
                />
              )}

              {/* View Modal */}
              {modalState.isViewModalOpen && (
                <ServiceDetailsPage
                  open={modalState.isViewModalOpen}
                  handleClose={handleModalClose}
                  data={modalState.selectedItem}
                />
              )}

              <DynamicTable
                columns={columns}
                data={servicesList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "delete"]} // Include "edit" in available actions
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListServicesPage;
